"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get AddFormChange () {
        return AddFormChange;
    },
    get AutomatedReviewCycle () {
        return AutomatedReviewCycle;
    },
    get BaseReviewCycle () {
        return BaseReviewCycle;
    },
    get EasyAddFormUpdate () {
        return EasyAddFormUpdate;
    },
    get ExternalFormUpdate () {
        return ExternalFormUpdate;
    },
    get FormChange () {
        return FormChange;
    },
    get GroupReviewCycle () {
        return GroupReviewCycle;
    },
    get ManualReviewCycle () {
        return ManualReviewCycle;
    },
    get RemoveFormChange () {
        return RemoveFormChange;
    },
    get ReviewCycle () {
        return ReviewCycle;
    },
    get ReviewCycleEmbeddedWorkflow () {
        return ReviewCycleEmbeddedWorkflow;
    },
    get ReviewCycleProgress () {
        return ReviewCycleProgress;
    },
    get ReviewCycleStatus () {
        return ReviewCycleStatus;
    },
    get ReviewCycleType () {
        return ReviewCycleType;
    },
    get ReviewCycleWithDefaults () {
        return ReviewCycleWithDefaults;
    },
    get StandardFormUpdate () {
        return StandardFormUpdate;
    },
    get UpdateFormChange () {
        return UpdateFormChange;
    }
});
const _zod = require("zod");
const _models = require("@performyard/models");
const _utilsisomorphic = require("@performyard/utils-isomorphic");
const _formassignment = require("../form-assignment");
const _reviewcyclehelper = require("../helpers/review-cycle-helper");
const ReviewCycleEmbeddedWorkflow = _zod.z.object({
    _id: _utilsisomorphic.transformObjectId,
    name: _zod.z.string(),
    forms: _zod.z.array(_zod.z.record(_zod.z.string(), _zod.z.unknown()))
});
const ReviewCycleStatus = _zod.z.enum([
    "active",
    "completed",
    "closed",
    "scheduled"
]);
const ReviewCycleType = _zod.z.enum([
    "manual",
    "automated",
    "group"
]);
const ReviewCycleProgress = _zod.z.object({
    completedReviews: _zod.z.number(),
    totalReviews: _zod.z.number()
});
const AddFormChange = _zod.z.object({
    type: _zod.z.literal("add-form"),
    formAssignment: _zod.z.union([
        _formassignment.DirectFormAssignmentInput,
        _formassignment.EasyAddFormConfigInput
    ])
});
const StandardFormRemove = _zod.z.object({
    formName: _zod.z.string(),
    /** Standard applies to reports created as standard and reports created via easy add configs */ formType: _zod.z.literal("standard"),
    easyAddFormConfigId: _zod.z.undefined().optional()
});
const ExternalFormRemove = _zod.z.object({
    formName: _zod.z.string(),
    formType: _zod.z.literal("external"),
    easyAddFormConfigId: _zod.z.undefined().optional()
});
const EasyAddFormConfigRemove = _zod.z.object({
    formName: _zod.z.undefined().optional(),
    formType: _zod.z.literal("easy-add"),
    easyAddFormConfigId: _utilsisomorphic.transformObjectId
});
const RemoveFormChange = _zod.z.object({
    type: _zod.z.literal("remove-form")
}).and(_zod.z.union([
    StandardFormRemove,
    ExternalFormRemove,
    EasyAddFormConfigRemove
])).refine((d)=>!d.formName || !!d.formType, {
    message: "Form type is required for removing form by name"
}).refine((d)=>!!d.easyAddFormConfigId !== !!d.formName, {
    message: "Only specify one of formName or easyAddConfigId"
});
const StandardFormUpdate = _zod.z.object({
    formName: _zod.z.string(),
    /** Standard applies to reports created as standard and reports created via easy add configs */ formType: _zod.z.literal("standard"),
    easyAddFormConfigId: _zod.z.undefined().optional(),
    authorsDueDate: _utilsisomorphic.transformDate.optional(),
    signersDueDate: _utilsisomorphic.transformDate.optional()
}).refine((d)=>d.authorsDueDate === undefined === (d.signersDueDate === undefined), {
    message: "Both due dates are required, or neither"
}).refine((d)=>d.authorsDueDate === undefined || d.signersDueDate === undefined || d.signersDueDate >= d.authorsDueDate, {
    message: "Signer's due date must be equal to or after the author's due date"
});
const ExternalFormUpdate = _zod.z.object({
    formName: _zod.z.string(),
    formType: _zod.z.literal("external"),
    easyAddFormConfigId: _zod.z.undefined().optional()
});
const EasyAddFormUpdate = _formassignment.EasyAddFormConfigInputBase// Update flow doesn't use these — formBaseId/easyAdd are identity, redacted/visible live on the outer UpdateFormChange
.omit({
    formBaseId: true,
    easyAdd: true,
    redacted: true,
    visible: true,
    isActive: true
}).partial().extend({
    formName: _zod.z.undefined().optional(),
    formType: _zod.z.literal("easy-add"),
    /** Easy add form config ID */ easyAddFormConfigId: _utilsisomorphic.transformObjectId,
    inactive: _zod.z.boolean().optional()
}).refine((d)=>d.whoCanEasyAdd !== "subject" || d.subjectEmployeeChooseAuthor !== undefined, {
    message: "subjectEmployeeChooseAuthor is required when whoCanEasyAdd is 'subject'"
}).refine((d)=>{
    const hasRelative = d.daysToCompleteAuthors !== undefined || d.daysToCompleteSigners !== undefined;
    const hasStatic = d.authorsDueDate !== undefined || d.signersDueDate !== undefined;
    // Partial updates may omit due date fields entirely; when provided, must be one mode only
    return !(hasRelative && hasStatic);
}, {
    message: "Cannot mix relative (daysToComplete*) and static (*DueDate) due dates in a single update"
}).refine((d)=>{
    // If author due date is being updated (either mode), signers must be provided
    const hasAuthorDate = d.daysToCompleteAuthors !== undefined || d.authorsDueDate !== undefined;
    return !hasAuthorDate || d.signers !== undefined;
}, {
    message: "signers must be explicitly set when updating author due date (even if it's empty)"
}).refine((d)=>{
    // If signers is empty, no signer due dates allowed
    if (d.signers === undefined || d.signers.length > 0) return true;
    return d.daysToCompleteSigners === undefined && d.signersDueDate === undefined;
}, {
    message: "Signer due dates must not be provided when signers is empty"
}).refine((d)=>{
    // If signers is non-empty and author due date is being updated, signer due date in matching mode is required
    if (d.signers === undefined || d.signers.length === 0) return true;
    if (d.daysToCompleteAuthors !== undefined) return d.daysToCompleteSigners !== undefined;
    if (d.authorsDueDate !== undefined) return d.signersDueDate !== undefined;
    return true;
}, {
    message: "Signer due date is required when signers is non-empty and updating author due date"
});
const UpdateFormChange = _zod.z.object({
    type: _zod.z.literal("update-form"),
    redacted: _zod.z.boolean().optional(),
    visible: _zod.z.boolean().optional()
}).and(_zod.z.union([
    StandardFormUpdate,
    ExternalFormUpdate,
    EasyAddFormUpdate
]));
const FormChange = _zod.z.union([
    AddFormChange,
    RemoveFormChange,
    UpdateFormChange
]);
const BaseReviewCycle = _zod.z.object({
    _id: _utilsisomorphic.transformObjectId,
    name: _zod.z.string(),
    workflow: ReviewCycleEmbeddedWorkflow.optional(),
    startDate: _utilsisomorphic.transformDate,
    adminsOnly: _zod.z.boolean().default(false),
    progress: ReviewCycleProgress,
    status: ReviewCycleStatus,
    jobStatus: _models.ReviewCycleJobStatusV2.optional(),
    easyAddConfigCount: _zod.z.number().default(0),
    completeDate: _utilsisomorphic.transformDate.nullable().optional(),
    createdAt: _models.ReviewCycleModelV2.shape.created_at,
    creator: _models.ReviewCycleModelV2.shape.creator,
    updatedAt: _models.ReviewCycleModelV2.shape.updated_at,
    updatedBy: _models.ReviewCycleModelV2.shape.updated_by
});
const ManualReviewCycle = BaseReviewCycle.extend({
    type: _zod.z.literal("manual")
});
const AutomatedReviewCycle = BaseReviewCycle.extend({
    type: _zod.z.literal("automated"),
    automationStatus: _models.ReviewCycleAutomationStatusV2.optional()
});
const GroupReviewCycle = BaseReviewCycle.extend({
    type: _zod.z.literal("group"),
    department: _zod.z.object({
        _id: _utilsisomorphic.transformObjectId,
        name: _zod.z.string()
    })
});
const ReviewCycle = _zod.z.discriminatedUnion("type", [
    ManualReviewCycle,
    AutomatedReviewCycle,
    GroupReviewCycle
]);
const ReviewCycleWithDefaults = _zod.z.preprocess((input)=>(0, _reviewcyclehelper.convertReviewCycleModelToServiceContract)(input), ReviewCycle);
